import { useCallback, useEffect, useRef, useState } from 'react';
import { createSessionIdleTimer } from './session-idle-timer';
import { bindIdleActivity } from './session-idle-timer';
import type { SessionIdleTimer } from './session-idle-timer';
import type { SessionClockUpdate } from './session-clock-headers';

export type SessionActivitySubscriber = (listener: (activity: SessionClockUpdate) => void) => () => void;

export type SessionTimeoutOptions = {
  idleTimeoutHours: number;
  warningMinutes: number;
  /** The server row's expiry on this browser's clock, as last reported by a response. */
  expiresAt?: number;
  /** Slides the server window; false means the session is already gone. */
  onExtend: () => Promise<boolean>;
  onExpired: () => void;
  /** A host whose every response carries the session clock feeds it in here, so the countdown
   *  follows the server rather than this tab's own guess. A host without one simply omits it. */
  subscribeToActivity?: SessionActivitySubscriber;
};

export type SessionTimeoutState = {
  showWarning: boolean;
  /** Seconds until this tab signs the user out — what the warning dialog counts down. */
  remainingSeconds: number;
  onStaySignedIn: () => void;
  onHideWarning: () => void;
};

export function useSessionTimeout(options: SessionTimeoutOptions): SessionTimeoutState {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(options.idleTimeoutHours * 60 * 60);
  const timerRef = useRef<SessionIdleTimer | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const timer = createSessionIdleTimer(
      {
        idleTimeoutHours: optionsRef.current.idleTimeoutHours,
        warningMinutes: optionsRef.current.warningMinutes,
      },
      {
        extend: () => optionsRef.current.onExtend(),
        onWarningChange: setShowWarning,
        onRemainingChange: setRemainingSeconds,
        onExpired: () => {
          optionsRef.current.onExpired();
        },
        now: () => Date.now(),
        setInterval: (callback, ms) => window.setInterval(callback, ms),
        clearInterval: (handle) => window.clearInterval(handle),
        setTimeout: (callback, ms) => window.setTimeout(callback, ms),
        clearTimeout: (handle) => window.clearTimeout(handle),
      }
    );
    timerRef.current = timer;

    const handleServerActivity = (activity: SessionClockUpdate) => {
      if (activity.policy) {
        timer.setPolicy(activity.policy);
      }
      if (activity.expiresAt !== undefined) {
        timer.setDeadline(activity.expiresAt);
      }
    };

    const unbindActivity = bindIdleActivity(timer, window, document);
    let unsubscribeFromServerActivity: () => void = () => undefined;
    if (optionsRef.current.subscribeToActivity) {
      unsubscribeFromServerActivity = optionsRef.current.subscribeToActivity(handleServerActivity);
    }
    timer.start();

    return () => {
      timer.stop();
      timerRef.current = null;
      unbindActivity();
      unsubscribeFromServerActivity();
    };
  }, []);

  useEffect(() => {
    if (timerRef.current) {
      timerRef.current.setPolicy({
        idleTimeoutHours: options.idleTimeoutHours,
        warningMinutes: options.warningMinutes,
      });
    }
  }, [options.idleTimeoutHours, options.warningMinutes]);

  useEffect(() => {
    if (timerRef.current && options.expiresAt !== undefined) {
      timerRef.current.setDeadline(options.expiresAt);
    }
  }, [options.expiresAt]);

  const onStaySignedIn = useCallback(() => {
    if (timerRef.current) {
      timerRef.current.staySignedIn();
    }
  }, []);

  const onHideWarning = useCallback(() => {
    if (timerRef.current) {
      timerRef.current.hideWarning();
    }
  }, []);

  return {
    showWarning: showWarning,
    remainingSeconds: remainingSeconds,
    onStaySignedIn: onStaySignedIn,
    onHideWarning: onHideWarning,
  };
}
